import { z } from 'zod';
import { AgreementTypeSchema, AgreementTemplateStatusSchema, } from './enums.schema.js';
/**
 * Phase 23 — operator template admin (23-1). Supersede pattern mirroring the
 * 10b/11c policy admin: create a DRAFT, publish it (DRAFT → ACTIVE, superseding
 * the prior ACTIVE of that type), deprecate. Content (`bodyHtml`) is DATA the
 * operator edits, so the reconciled legal text swaps in with no deploy (23-8).
 */
const VersionSchema = z
    .string()
    .regex(/^v\d+-\d{4}-\d{2}$/, 'Version must be v{n}-{YYYY-MM}, e.g. v2-2026-09');
const ReasonSchema = z
    .string()
    .trim()
    .min(10, 'Reason must be at least 10 characters — document why')
    .max(500);
// ===== create draft =====
export const AdminAgreementTemplateCreateRequestSchema = z.object({
    type: AgreementTypeSchema,
    version: VersionSchema,
    title: z.string().trim().min(3).max(200),
    bodyHtml: z.string().min(1),
    /** When false, publishing this version does NOT force existing signers to
     *  re-accept (23-7) — a minor/non-material revision. */
    requiresReacceptance: z.boolean().default(true),
});
// ===== update (DRAFT only — edit content before publish/discard) =====
// Only the content (title + body) is editable; type/version are identity and
// stay fixed. A DRAFT has no signers, so editing it in place is safe (mirrors
// the rate "unused ⇒ editable" rule). The service re-sanitizes + re-hashes.
export const AdminAgreementTemplateUpdateRequestSchema = z.object({
    title: z.string().trim().min(3).max(200),
    bodyHtml: z.string().min(1),
    /** Whether publishing THIS version will force existing signers to re-accept
     *  (the "Re-accept?" column). Editable on a DRAFT; frozen once ACTIVE. */
    requiresReacceptance: z.boolean(),
});
// ===== publish (DRAFT → ACTIVE, supersede prior) =====
export const AdminAgreementTemplatePublishRequestSchema = z.object({
    reason: ReasonSchema,
});
// ===== deprecate =====
export const AdminAgreementTemplateDeprecateRequestSchema = z.object({
    reason: ReasonSchema,
});
// ===== list / detail =====
export const AdminAgreementTemplateListQuerySchema = z.object({
    type: AgreementTypeSchema.optional(),
    status: AgreementTemplateStatusSchema.optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(25),
});
export const AdminAgreementTemplateItemSchema = z.object({
    id: z.string().uuid(),
    type: AgreementTypeSchema,
    version: z.string(),
    title: z.string(),
    status: AgreementTemplateStatusSchema,
    contentHash: z.string(),
    requiresReacceptance: z.boolean(),
    effectiveFrom: z.string().datetime().nullable(),
    supersededAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const AdminAgreementTemplateListResponseSchema = z.object({
    items: z.array(AdminAgreementTemplateItemSchema),
    totalCount: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
// detail = item + the full body (list omits body for payload size)
export const AdminAgreementTemplateDetailResponseSchema = AdminAgreementTemplateItemSchema.extend({
    bodyHtml: z.string(),
});
// ===== change log =====
export const AdminAgreementTemplateChangeLogQuerySchema = z.object({
    templateId: z.string().uuid().optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(25),
});
export const AdminAgreementTemplateChangeLogItemSchema = z.object({
    id: z.string().uuid(),
    templateId: z.string().uuid(),
    action: z.string(),
    actorUserId: z.string().uuid(),
    actorEmail: z.string(),
    reason: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const AdminAgreementTemplateChangeLogResponseSchema = z.object({
    items: z.array(AdminAgreementTemplateChangeLogItemSchema),
    totalCount: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
// ===== operator read of a subject's acceptance (+ signed-PDF download) =====
export const AdminAgreementAcceptanceItemSchema = z.object({
    id: z.string().uuid(),
    agreementType: AgreementTypeSchema,
    subjectUserId: z.string().uuid(),
    versionAccepted: z.string(),
    contentHash: z.string(),
    typedLegalName: z.string(),
    ipAddress: z.string().nullable(),
    acceptedAt: z.string().datetime(),
    /** Signed-URL to the signed PDF, if rendered (null until async render). */
    signedPdfUrl: z.string().nullable(),
});
export const AdminAgreementAcceptanceListResponseSchema = z.object({
    items: z.array(AdminAgreementAcceptanceItemSchema),
});
//# sourceMappingURL=admin.schema.js.map